import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';

type Side = 'w' | 'b';
type PieceType = 'K' | 'Q' | 'R' | 'B' | 'N' | 'P';
type Piece = { c: Side; t: PieceType } | null;

export const CH_GLYPH: Record<PieceType, string> = {
  K: '♚', Q: '♛', R: '♜', B: '♝', N: '♞', P: '♟',
};

function chStart(): Piece[] {
  const back: PieceType[] = ['R', 'N', 'B', 'Q', 'K', 'B', 'N', 'R'];
  const b: Piece[] = Array(64).fill(null);
  for (let c = 0; c < 8; c++) {
    b[c] = { c: 'b', t: back[c] }; b[8 + c] = { c: 'b', t: 'P' };
    b[48 + c] = { c: 'w', t: 'P' }; b[56 + c] = { c: 'w', t: back[c] };
  }
  return b;
}

const rc = (i: number): [number, number] => [Math.floor(i / 8), i % 8];
const at = (b: Piece[], r: number, c: number): Piece | undefined =>
  (r >= 0 && r < 8 && c >= 0 && c < 8 ? b[r * 8 + c] : undefined);

function moves(b: Piece[], i: number): number[] {
  const p = b[i]; if (!p) return [];
  const [r, c] = rc(i); const out: number[] = []; const me = p.c;
  const push = (r2: number, c2: number): boolean => {
    if (r2 < 0 || r2 > 7 || c2 < 0 || c2 > 7) return false;
    const t = b[r2 * 8 + c2];
    if (!t) { out.push(r2 * 8 + c2); return true; }
    if (t.c !== me) out.push(r2 * 8 + c2);
    return false;
  };
  const ray = (dr: number, dc: number): void => {
    let r2 = r + dr, c2 = c + dc;
    while (r2 >= 0 && r2 < 8 && c2 >= 0 && c2 < 8) {
      const t = b[r2 * 8 + c2];
      if (!t) out.push(r2 * 8 + c2);
      else { if (t.c !== me) out.push(r2 * 8 + c2); break; }
      r2 += dr; c2 += dc;
    }
  };
  if (p.t === 'P') {
    const dir = me === 'w' ? -1 : 1, start = me === 'w' ? 6 : 1;
    if (at(b, r + dir, c) === null) {
      out.push((r + dir) * 8 + c);
      if (r === start && at(b, r + 2 * dir, c) === null) out.push((r + 2 * dir) * 8 + c);
    }
    [-1, 1].forEach((dc) => { const t = at(b, r + dir, c + dc); if (t && t.c !== me) out.push((r + dir) * 8 + (c + dc)); });
  } else if (p.t === 'N') {
    ([[-2, -1], [-2, 1], [2, -1], [2, 1], [-1, -2], [-1, 2], [1, -2], [1, 2]] as const)
      .forEach(([dr, dc]) => push(r + dr, c + dc));
  } else if (p.t === 'K') {
    [-1, 0, 1].forEach((dr) => [-1, 0, 1].forEach((dc) => { if (dr || dc) push(r + dr, c + dc); }));
  } else {
    if (p.t === 'R' || p.t === 'Q') { ray(-1, 0); ray(1, 0); ray(0, -1); ray(0, 1); }
    if (p.t === 'B' || p.t === 'Q') { ray(-1, -1); ray(-1, 1); ray(1, -1); ray(1, 1); }
  }
  return out;
}

/**
 * Mini Chess — pseudo-legal-move hotseat chess (no check detection; auto-queen
 * promotion). Ported from the OTA-B2B-6 design (games2.jsx). Rendered in the
 * Toolbox Games gallery (floating). Default (Emulated) encapsulation keeps its
 * `ch-*` board CSS scoped — no clash with the Entertainment Chess.
 */
@Component({
  selector: 'app-mini-chess',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './mini-chess.component.html',
  styleUrl: './mini-chess.component.scss',
})
export class MiniChessComponent {
  readonly CH_GLYPH = CH_GLYPH;

  readonly board = signal<Piece[]>(chStart());
  readonly turn = signal<Side>('w');
  readonly sel = signal<number | null>(null);
  readonly caps = signal<Record<Side, PieceType[]>>({ w: [], b: [] });

  readonly legal = computed<number[]>(() => {
    const s = this.sel();
    return s != null ? moves(this.board(), s) : [];
  });

  readonly trayCaps = computed<PieceType[]>(() => {
    const t = this.turn();
    return this.caps()[t === 'w' ? 'b' : 'w'];
  });

  rowCol = rc;

  isSel(i: number): boolean { return this.sel() === i; }
  isLegal(i: number): boolean { return this.legal().includes(i); }
  isCap(i: number): boolean { const p = this.board()[i]; return !!p && this.isLegal(i); }

  sqClass(i: number): string {
    const [r, c] = rc(i);
    return 'ch-sq ' + ((r + c) % 2 ? 'dark' : 'light')
      + (this.isSel(i) ? ' sel' : '')
      + (this.isLegal(i) ? (this.isCap(i) ? ' cap' : '') : '');
  }

  clickSq(i: number): void {
    const board = this.board();
    const p = board[i];
    const sel = this.sel();
    const turn = this.turn();
    if (sel != null && this.legal().includes(i)) {
      const nb = board.slice(); const moved = nb[sel]!; const target = nb[i];
      if (target) this.caps.update((cp) => ({ ...cp, [turn]: [...cp[turn], target.t] }));
      nb[i] = moved; nb[sel] = null;
      if (moved.t === 'P' && (i < 8 || i >= 56)) nb[i] = { c: moved.c, t: 'Q' }; // auto-queen
      this.board.set(nb); this.sel.set(null); this.turn.set(turn === 'w' ? 'b' : 'w');
      return;
    }
    if (p && p.c === turn) this.sel.set(i === sel ? null : i); else this.sel.set(null);
  }

  reset(): void {
    this.board.set(chStart());
    this.turn.set('w');
    this.sel.set(null);
    this.caps.set({ w: [], b: [] });
  }
}
